import { useState } from 'react';
import { ApiError, api } from '../api/client';
import { useAuth } from '../auth/AuthProvider';
import { useAsync } from '../hooks/useAsync';
import { useT } from '../i18n/I18nProvider';
import { AsyncError, Banner, EmptyState, Loading } from './states';

interface Invitation {
  id: string;
  prefix: string;
  status: string;
  recipientEmail: string | null;
  recipientHint: string | null;
  expiresAt: string;
  responseNote: string | null;
  revisions: Array<{ revision: number; proposedBy: string; termsHash: string }>;
}

interface Change {
  field: string;
  from: string;
  to: string;
  direction: string;
}

interface Review {
  prefix: string;
  inviterOrganization: string;
  inviterFederationId: string;
  inviterVerified: boolean;
  expiresAt: string;
  revision: number;
  termsHash: string;
  awaiting: 'INVITER' | 'RECIPIENT';
  changes: Change[];
  expandsAccess: boolean;
  risks: string[];
  terms: {
    connectionType: string;
    transitPermitted: boolean;
    internetBreakoutRequested: boolean;
    monitoringVisibility: string;
    durationDays: number | null;
    message: string;
    networks: Array<{ cidr: string; direction: string; description: string }>;
    services: Array<{ host: string; port: number; protocol: string; direction: string }>;
  };
}

/**
 * Invitations, from both ends.
 *
 * The outgoing side shows only a non-secret prefix, because the code itself is
 * returned once at creation and the console never holds it again. The incoming
 * side is addressed by that code rather than by a link into a list, which is what
 * keeps outstanding invitations non-enumerable.
 *
 * The review panel leads with the risk summary rather than the inviter's message.
 * The message is what the inviter chose to say; the risks are what the proposal
 * actually does, and a recipient deciding between them should read the second
 * first.
 */
export function InvitationsPanel({ organizationId }: { organizationId?: string }): JSX.Element {
  const t = useT();
  const { can } = useAuth();
  const mayWrite = can('federation:write');

  const outgoing = useAsync<Invitation[]>(
    () => api.get<Invitation[]>('/federation/invitations'),
    [],
  );

  const [issued, setIssued] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [transit, setTransit] = useState(false);
  const [recipientEmail, setRecipientEmail] = useState('');

  const [code, setCode] = useState('');
  const [review, setReview] = useState<Review | null>(null);
  const [reviewError, setReviewError] = useState<string | null>(null);
  const [answered, setAnswered] = useState<string | null>(null);
  const [counterNote, setCounterNote] = useState('');

  const run = async (fn: () => Promise<unknown>): Promise<void> => {
    setError(null);
    setBusy(true);
    try {
      await fn();
      outgoing.reload();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('errors.unexpected'));
    } finally {
      setBusy(false);
    }
  };

  const create = (e: React.FormEvent): void => {
    e.preventDefault();
    if (!organizationId) return;
    setIssued(null);
    void run(async () => {
      const res = await api.post<{ secret: string }>('/federation/invitations', {
        organizationId,
        connectionType: 'SITE_TO_SITE',
        terms: { transitPermitted: transit },
        ...(recipientEmail.trim() ? { recipientEmail: recipientEmail.trim() } : {}),
      });
      setIssued(res.secret);
      setRecipientEmail('');
      setTransit(false);
    });
  };

  const lookUp = (e: React.FormEvent): void => {
    e.preventDefault();
    setReviewError(null);
    setAnswered(null);
    setBusy(true);
    api
      .post<Review>('/federation/invitations/review', { secret: code.trim() })
      .then((r) => setReview(r))
      .catch((err: unknown) =>
        setReviewError(err instanceof ApiError ? err.message : t('errors.unexpected')),
      )
      .finally(() => setBusy(false));
  };

  const counter = (): void => {
    setBusy(true);
    setReviewError(null);
    api
      .post('/federation/invitations/counter', {
        secret: code.trim(),
        terms: { transitPermitted: false, internetBreakoutRequested: false },
        note: counterNote,
      })
      .then(() => api.post<Review>('/federation/invitations/review', { secret: code.trim() }))
      .then((r) => {
        setReview(r);
        setCounterNote('');
      })
      .catch((err: unknown) =>
        setReviewError(err instanceof ApiError ? err.message : t('errors.unexpected')),
      )
      .finally(() => setBusy(false));
  };

  const respond = (action: 'accept' | 'reject'): void => {
    setBusy(true);
    setReviewError(null);
    api
      .post(`/federation/invitations/${action}`, {
        secret: code.trim(),
        // Naming the revision is what stops an acceptance landing on terms that
        // changed between reading and clicking.
        ...(action === 'accept' ? { termsHash: review?.termsHash } : {}),
      })
      .then(() => {
        setAnswered(action);
        setReview(null);
        setCode('');
      })
      .catch((err: unknown) =>
        setReviewError(err instanceof ApiError ? err.message : t('errors.unexpected')),
      )
      .finally(() => setBusy(false));
  };

  return (
    <>
      <div className="card">
        <h2>{t('invitations.outgoing')}</h2>
        <p>{t('invitations.outgoingIntro')}</p>
        {error ? <Banner variant="error">{error}</Banner> : null}
        {issued ? (
          <Banner variant="warning">
            {t('invitations.codeOnce')} <code>{issued}</code>
          </Banner>
        ) : null}

        {mayWrite && organizationId ? (
          <form className="card form" onSubmit={create}>
            <h3>{t('invitations.create')}</h3>
            <label className="field">
              <span>{t('invitations.recipientEmail')}</span>
              <input
                value={recipientEmail}
                onChange={(e) => setRecipientEmail(e.target.value)}
                placeholder="partner@example.com"
              />
            </label>
            <label className="field field--check">
              <input
                type="checkbox"
                checked={transit}
                onChange={(e) => setTransit(e.target.checked)}
              />
              <span>{t('invitations.proposeTransit')}</span>
            </label>
            <p className="hint">{t('invitations.defaultsHint')}</p>
            <button type="submit" className="btn" disabled={busy}>
              {t('invitations.send')}
            </button>
          </form>
        ) : null}

        {outgoing.loading ? (
          <Loading />
        ) : outgoing.error ? (
          <AsyncError error={outgoing.error} onRetry={outgoing.reload} />
        ) : !outgoing.data || outgoing.data.length === 0 ? (
          <EmptyState hint={t('invitations.none')} />
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>{t('invitations.code')}</th>
                  <th>{t('invitations.recipient')}</th>
                  <th>{t('invitations.status')}</th>
                  <th>{t('invitations.expires')}</th>
                  <th aria-label="actions" />
                </tr>
              </thead>
              <tbody>
                {outgoing.data.map((i) => (
                  <tr key={i.id}>
                    <td>
                      <code>{i.prefix}…</code>
                    </td>
                    <td>{i.recipientEmail ?? i.recipientHint ?? '—'}</td>
                    <td>
                      {t(`invitations.states.${i.status}`)}
                      {i.responseNote ? <div className="hint">{i.responseNote}</div> : null}
                    </td>
                    <td>{new Date(i.expiresAt).toLocaleDateString()}</td>
                    <td className="row-actions">
                      {mayWrite && (i.status === 'SENT' || i.status === 'VIEWED') ? (
                        <button
                          type="button"
                          className="btn btn--ghost btn--danger"
                          disabled={busy}
                          onClick={() =>
                            void run(() => api.post(`/federation/invitations/${i.id}/revoke`, {}))
                          }
                        >
                          {t('invitations.revoke')}
                        </button>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="card">
        <h2>{t('invitations.incoming')}</h2>
        <p>{t('invitations.incomingIntro')}</p>
        {reviewError ? <Banner variant="error">{reviewError}</Banner> : null}
        {answered ? (
          <Banner variant="info">
            {answered === 'accept' ? t('invitations.accepted') : t('invitations.rejected')}
          </Banner>
        ) : null}

        <form className="form form--inline" onSubmit={lookUp}>
          <label className="field">
            <span>{t('invitations.code')}</span>
            <input value={code} onChange={(e) => setCode(e.target.value)} required minLength={16} />
          </label>
          <button type="submit" className="btn" disabled={busy}>
            {t('invitations.look')}
          </button>
        </form>

        {review ? (
          <div className="card">
            <h3>{t('invitations.proposal')}</h3>
            <dl className="detail-grid">
              <dt>{t('invitations.from')}</dt>
              <dd>
                {review.inviterOrganization}{' '}
                <span
                  className={`chip ${review.inviterVerified ? 'chip--online' : 'chip--warning'}`}
                >
                  {review.inviterVerified
                    ? t('federation.verified')
                    : t('invitations.inviterUnverified')}
                </span>
              </dd>
              <dt>{t('invitations.connectionType')}</dt>
              <dd>{review.terms.connectionType}</dd>
              <dt>{t('invitations.networks')}</dt>
              <dd>
                {review.terms.networks.length === 0
                  ? t('invitations.noNetworks')
                  : review.terms.networks.map((n) => `${n.cidr} (${n.direction})`).join(', ')}
              </dd>
              <dt>{t('invitations.monitoring')}</dt>
              <dd>
                {t(`agreements.visibility.${review.terms.monitoringVisibility}`)}{' '}
                <small>{t('invitations.monitoringEnforced')}</small>
              </dd>
              <dt>{t('invitations.transit')}</dt>
              <dd>{review.terms.transitPermitted ? t('invitations.yes') : t('invitations.no')}</dd>
              <dt>{t('invitations.expires')}</dt>
              <dd>{new Date(review.expiresAt).toLocaleString()}</dd>
            </dl>

            {review.changes.length > 0 ? (
              <>
                <h4>{t('invitations.changed')}</h4>
                {review.expandsAccess ? (
                  <Banner variant="warning">{t('invitations.expandsAccess')}</Banner>
                ) : null}
                <div className="table-wrap">
                  <table>
                    <thead>
                      <tr>
                        <th>{t('invitations.field')}</th>
                        <th>{t('invitations.wasValue')}</th>
                        <th>{t('invitations.nowValue')}</th>
                        <th>{t('invitations.effect')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {review.changes.map((c) => (
                        <tr key={`${c.field}-${c.from}-${c.to}`}>
                          <td>{c.field}</td>
                          <td>{c.from}</td>
                          <td>{c.to}</td>
                          <td>{t(`invitations.directions.${c.direction}`)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            ) : null}

            <h4>{t('invitations.risks')}</h4>
            <ul>
              {review.risks.map((r) => (
                <li key={r}>{r}</li>
              ))}
            </ul>

            {review.terms.message ? (
              <>
                <h4>{t('invitations.message')}</h4>
                <p>{review.terms.message}</p>
              </>
            ) : null}

            {review.awaiting !== 'RECIPIENT' ? (
              <Banner variant="info">{t('invitations.awaitingThem')}</Banner>
            ) : null}

            {mayWrite && review.awaiting === 'RECIPIENT' ? (
              <div className="row-actions">
                <button
                  type="button"
                  className="btn"
                  disabled={busy}
                  onClick={() => respond('accept')}
                >
                  {t('invitations.accept')}
                </button>
                <button type="button" className="btn btn--ghost" disabled={busy} onClick={counter}>
                  {t('invitations.counter')}
                </button>
                <button
                  type="button"
                  className="btn btn--ghost btn--danger"
                  disabled={busy}
                  onClick={() => respond('reject')}
                >
                  {t('invitations.reject')}
                </button>
              </div>
            ) : (
              <p className="hint">{t('invitations.readOnly')}</p>
            )}
          </div>
        ) : null}
      </div>
    </>
  );
}
